import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Trophy, ChevronRight, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import FightCard from "@/components/FightCard";
import SiteNav from "@/components/SiteNav";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_BG = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/tf9we5d6_hamara8-220.jpg";
const EVENT_LOGO = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/rof1u6za_LOGO_HAMARA_TUMMA.png";
const PLACEHOLDER_FIGHTER = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/rof1u6za_LOGO_HAMARA_TUMMA.png";

export default function HomePage({ predictions, setPredictions, bonusPicks, setBonusPicks }) {
  const [fights, setFights] = useState([]);
  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [myPredictions, setMyPredictions] = useState(null);
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [texts, setTexts] = useState({
    hero_description: "", marketing_checkbox: "", confirmation_title: "VEIKKAUS LUKITTU",
    confirmation_text: "", submit_heading: "LÄHETÄ VEIKKAUKSET", submit_button: "Lähetä veikkaukset",
    fight_card_heading: "OTTELUKORTTI", bonus_heading: "BONUS",
  });

  const fetchMyPredictions = useCallback(async (emailToLookup) => {
    try {
      setLookupLoading(true);
      const res = await axios.get(`${API}/my-predictions?email=${encodeURIComponent(emailToLookup)}`);
      if (res.data.found) {
        setMyPredictions(res.data);
        localStorage.setItem("mma_prediction_email", emailToLookup);
      } else {
        setMyPredictions(null);
      }
    } catch {
      setMyPredictions(null);
    } finally {
      setLookupLoading(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/fights`).then(res => setFights(res.data)).catch(() => toast.error("Otteluiden lataus epäonnistui")).finally(() => setLoading(false));
    axios.get(`${API}/site-content`).then(res => setTexts(prev => ({ ...prev, ...res.data.texts }))).catch(err => console.error("Failed to load site content:", err));
    const savedEmail = localStorage.getItem("mma_prediction_email");
    if (savedEmail) {
      fetchMyPredictions(savedEmail);
    }
  }, [fetchMyPredictions]);

  const handleLookup = (e) => {
    e.preventDefault();
    if (!lookupEmail) { toast.error("Syötä sähköpostiosoitteesi"); return; }
    fetchMyPredictions(lookupEmail);
  };

  const handleSelectWinner = (fightId, winner) => setPredictions(prev => ({ ...prev, [fightId]: winner }));
  const handleBonusPick = (key, val) => setBonusPicks(prev => ({ ...prev, [key]: val }));

  const undecidedFights = fights.filter(f => !f.result);
  const allUndecidedPicked = undecidedFights.length > 0 && undecidedFights.every(f => predictions[f.id]);

  const allFighters = useMemo(() => {
    const fighters = [];
    fights.forEach(f => { if (f.fighter1?.name) fighters.push(f.fighter1); if (f.fighter2?.name) fighters.push(f.fighter2); });
    return fighters;
  }, [fights]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Syötä sähköpostiosoitteesi"); return; }
    if (!allUndecidedPicked) { toast.error("Veikkaa kaikki ottelut"); return; }
    setSubmitting(true);
    try {
      const predictionData = Object.entries(predictions).map(([fight_id, winner]) => ({ fight_id, winner }));
      const bonusData = [{ question: "Nopein lopetus", fighter_name: bonusPicks.fastest_finish || null }];
      if (bonusPicks.fastest_round || bonusPicks.fastest_minute || bonusPicks.fastest_second) {
        bonusData.push({ question: "Nopein lopetus — Erä & aika", fighter_name: `Erä ${bonusPicks.fastest_round || '?'}, ${bonusPicks.fastest_minute || '0'}:${(bonusPicks.fastest_second || '0').padStart(2, '0')}` });
      }
      await axios.post(`${API}/predictions`, { email, predictions: predictionData, bonus_picks: bonusData, marketing_consent: marketingConsent });
      localStorage.setItem("mma_prediction_email", email.toLowerCase());
      setSubmitted(true);
      toast.success("Veikkaukset lähetetty!");
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(msg?.includes("already submitted") ? "Tämä sähköposti on jo käytetty veikkaukseen" : msg || "Lähetys epäonnistui");
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-md" data-testid="submission-confirmation">
          <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-6"><Trophy className="w-10 h-10 text-white" /></div>
          <h1 className="text-4xl sm:text-5xl uppercase tracking-tight mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{texts.confirmation_title}</h1>
          <p className="text-[#A1A1AA] mb-6">{texts.confirmation_text}</p>
          <div className="flex flex-col gap-3 items-center">
            <Button onClick={() => { setSubmitted(false); fetchMyPredictions(email); }}
              className="bg-[#FF3B30] text-white hover:bg-[#FF5A52] font-bold uppercase tracking-wider px-8 py-4 rounded-none" data-testid="view-my-predictions-btn">
              Tarkista veikkauksesi
            </Button>
            <Button onClick={() => window.location.reload()}
              className="bg-transparent border border-white/20 text-white hover:bg-white/5 font-bold uppercase tracking-wider px-8 py-4 rounded-none" data-testid="back-home-btn">
              Takaisin
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero with Nav */}
      <section className="relative min-h-[40vh] sm:min-h-[45vh] flex flex-col items-center justify-center overflow-hidden"
        style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: '30% 25%' }}>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.6) 75%, rgba(10,10,10,1) 100%)' }} />
        <div className="relative z-10 text-center px-4 py-12">
          <img src={EVENT_LOGO} alt="Hamara MMA" className="h-10 sm:h-14 lg:h-16 mx-auto mb-5 object-contain" data-testid="event-logo" />
          <p className="text-white/60 max-w-lg mx-auto text-xs sm:text-sm lg:text-base leading-relaxed">{texts.hero_description}</p>
          <SiteNav />
        </div>
      </section>

      {/* My Predictions Score Banner */}
      {myPredictions && (
        <section className="px-4 md:px-8 pt-6" data-testid="my-predictions-section">
          <div className="max-w-7xl mx-auto">
            <div className="bg-[#121212] border border-white/10 px-4 py-3 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Trophy className="w-5 h-5 text-[#FF3B30]" />
                <div>
                  <span className="text-sm text-white/60">Veikkauksesi — </span>
                  <span className="text-sm text-white">{myPredictions.email}</span>
                  {myPredictions.total_scored > 0 && (
                    <span className="ml-3 text-lg font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                      <span className="text-[#FF3B30]">{myPredictions.correct}</span>
                      <span className="text-white/40">/{myPredictions.total_scored} oikein</span>
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => { setMyPredictions(null); localStorage.removeItem("mma_prediction_email"); }}
                className="text-xs text-white/40 hover:text-white/60 transition-colors underline" data-testid="clear-my-predictions">
                Kirjaudu ulos
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Prediction Lookup (for returning users without localStorage) */}
      {!myPredictions && (
        <section className="py-4 px-4 md:px-8">
          <div className="max-w-md mx-auto">
            <form onSubmit={handleLookup} className="flex items-center gap-2" data-testid="prediction-lookup-form">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input type="email" placeholder="Tarkista veikkauksesi — syötä sähköposti" value={lookupEmail}
                  onChange={(e) => setLookupEmail(e.target.value)}
                  className="bg-transparent border border-white/10 text-white text-sm rounded-none pl-9 h-10 focus:border-white/30 placeholder:text-white/30"
                  data-testid="lookup-email-input" />
              </div>
              <Button type="submit" disabled={lookupLoading}
                className="bg-transparent border border-white/20 text-white/60 hover:text-white hover:bg-white/5 rounded-none h-10 px-4 text-xs uppercase tracking-wider"
                data-testid="lookup-submit-btn">
                {lookupLoading ? "..." : "Hae"}
              </Button>
            </form>
          </div>
        </section>
      )}

      {/* Fight Card */}
      <section className="py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-white/10" />
            <h2 className="text-2xl sm:text-3xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }} data-testid="fights-heading">{texts.fight_card_heading}</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          {loading ? (
            <div className="text-center py-12 text-[#A1A1AA]" data-testid="loading-indicator">Ladataan otteluita...</div>
          ) : fights.length === 0 ? (
            <div className="text-center py-8" data-testid="no-fights-message"><p className="text-[#A1A1AA]">Ottelukorttia ei ole vielä julkaistu</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fights-grid">
              {fights.map((fight, i) => {
                const userPick = myPredictions?.picks?.find(p => p.fight_id === fight.id) || null;
                return (
                  <div key={fight.id} className="fight-card-animate" style={{ animationDelay: `${i * 0.08}s` }}>
                    <FightCard fight={fight} selectedWinner={myPredictions ? (userPick?.winner || null) : predictions[fight.id]}
                      onSelectWinner={(w) => handleSelectWinner(fight.id, w)} fightNumber={i + 1}
                      userPick={userPick} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bonus — Nopein lopetus */}
      {fights.length > 0 && (
        <section className="py-8 px-4 md:px-8 border-t border-white/10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px flex-1 bg-white/10" />
              <h2 className="text-2xl sm:text-3xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{texts.bonus_heading}</h2>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <div className="max-w-md mx-auto">
              {myPredictions ? (
                /* Read-only bonus picks view */
                <div className="bg-[#121212] border border-white/10 p-4" data-testid="bonus-readonly">
                  <label className="text-xs uppercase tracking-[0.15em] text-[#FF3B30] font-bold block mb-3">Nopein lopetus</label>
                  {(myPredictions.bonus_picks || []).map((pick) => (
                    <div key={pick.question} className="py-2 border-b border-white/5 last:border-b-0">
                      <span className="text-[10px] uppercase tracking-wider text-[#A1A1AA] block">{pick.question}</span>
                      <span className="text-sm font-bold uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                        {pick.fighter_name || "—"}
                      </span>
                    </div>
                  ))}
                  {(!myPredictions.bonus_picks || myPredictions.bonus_picks.length === 0) && (
                    <p className="text-sm text-white/30">Ei bonusveikkauksia</p>
                  )}
                </div>
              ) : (
                /* Interactive bonus form */
                <div className="bg-[#121212] border border-white/10 p-4" data-testid="bonus-bet-fastest">
                <label className="text-xs uppercase tracking-[0.15em] text-[#FF3B30] font-bold block mb-3">Nopein lopetus</label>
                <Select value={bonusPicks.fastest_finish || ""} onValueChange={(val) => handleBonusPick('fastest_finish', val)}>
                  <SelectTrigger className="bg-transparent border border-white/20 text-white rounded-none focus:ring-0 focus:ring-offset-0 focus:border-white/40" data-testid="bonus-select-fastest">
                    <SelectValue placeholder="Valitse ottelija" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border border-white/20 text-white rounded-none max-h-48 overflow-y-auto">
                    {allFighters.map(f => <SelectItem key={f.name} value={f.name} className="focus:bg-white/10 focus:text-white rounded-none">{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 mt-3">
                  <div className="flex-1">
                    <label className="text-[9px] uppercase tracking-wider text-[#A1A1AA] block mb-1">Erä</label>
                    <Select value={bonusPicks.fastest_round || ""} onValueChange={(val) => handleBonusPick('fastest_round', val)}>
                      <SelectTrigger className="bg-transparent border border-white/20 text-white rounded-none focus:ring-0 h-9 text-xs"><SelectValue placeholder="Erä" /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border border-white/20 text-white rounded-none">
                        {[1,2,3].map(r => <SelectItem key={r} value={String(r)} className="focus:bg-white/10 focus:text-white rounded-none">Erä {r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="text-[9px] uppercase tracking-wider text-[#A1A1AA] block mb-1">Min</label>
                    <Select value={bonusPicks.fastest_minute || ""} onValueChange={(val) => handleBonusPick('fastest_minute', val)}>
                      <SelectTrigger className="bg-transparent border border-white/20 text-white rounded-none focus:ring-0 h-9 text-xs"><SelectValue placeholder="Min" /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border border-white/20 text-white rounded-none">
                        {[0,1,2,3,4,5].map(m => <SelectItem key={m} value={String(m)} className="focus:bg-white/10 focus:text-white rounded-none">{m} min</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="text-[9px] uppercase tracking-wider text-[#A1A1AA] block mb-1">Sek</label>
                    <Select value={bonusPicks.fastest_second || ""} onValueChange={(val) => handleBonusPick('fastest_second', val)}>
                      <SelectTrigger className="bg-transparent border border-white/20 text-white rounded-none focus:ring-0 h-9 text-xs"><SelectValue placeholder="Sek" /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border border-white/20 text-white rounded-none">
                        {Array.from({length: 12}, (_, i) => i * 5).map(s => <SelectItem key={s} value={String(s)} className="focus:bg-white/10 focus:text-white rounded-none">{String(s).padStart(2, '0')} sek</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {bonusPicks.fastest_finish && (() => {
                  const pf = allFighters.find(f => f.name === bonusPicks.fastest_finish);
                  return pf ? (
                    <div className="mt-3 text-center pt-3 border-t border-white/10">
                      <div className="w-14 h-14 mx-auto mb-2 overflow-hidden rounded-full ring-1 ring-white/20">
                        <img src={pf.image_url || PLACEHOLDER_FIGHTER} alt={pf.name} className="w-full h-full object-cover object-top grayscale" />
                      </div>
                      <p className="text-sm uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{pf.name}</p>
                    </div>
                  ) : null;
                })()}
              </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Submit */}
      {undecidedFights.length > 0 && !myPredictions && (
        <section className="py-10 px-4 md:px-8 border-t border-white/10">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl sm:text-3xl uppercase tracking-tight text-center mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{texts.submit_heading}</h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="prediction-form">
              <div className="relative">
                <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input type="email" placeholder="Sähköpostiosoite" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="bg-transparent border-0 border-b-2 border-white/20 text-white focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-[#FF3B30] transition-colors py-3 pl-8 placeholder:text-white/40 text-lg rounded-none" data-testid="email-input" />
              </div>
              <div className="text-left bg-[#121212] border border-white/10 p-4">
                <div className="flex items-start gap-3">
                  <Checkbox id="marketing-consent" checked={marketingConsent} onCheckedChange={setMarketingConsent}
                    className="mt-0.5 border-white/30 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]" data-testid="marketing-consent-checkbox" />
                  <Label htmlFor="marketing-consent" className="text-sm cursor-pointer leading-relaxed text-white">{texts.marketing_checkbox}</Label>
                </div>
              </div>
              <div className="text-center">
                <p className="text-xs text-[#A1A1AA] mb-3">{allUndecidedPicked ? "Kaikki veikkaukset tehty — valmis lähetettäväksi!" : `${Object.keys(predictions).length}/${undecidedFights.length} ottelua veikattuna`}</p>
                <Button type="submit" disabled={!allUndecidedPicked || submitting}
                  className="bg-[#FF3B30] text-white hover:bg-[#FF5A52] font-bold uppercase tracking-wider px-10 py-5 transition-colors duration-200 shadow-[0_0_15px_rgba(255,59,48,0.3)] hover:shadow-[0_0_25px_rgba(255,59,48,0.5)] rounded-none disabled:opacity-50 disabled:cursor-not-allowed btn-shine" data-testid="submit-prediction-button">
                  {submitting ? "Lähetetään..." : texts.submit_button}<ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </form>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="w-10 h-10 border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-colors"
              data-testid="scroll-to-top">
              <ChevronRight className="w-5 h-5 -rotate-90" />
            </button>
            <span className="text-xs text-[#A1A1AA]">Hamara MMA Vol 9</span>
          </div>
          <Link to="/admin" className="text-xs text-white/30 hover:text-white/50 transition-colors" data-testid="admin-link">Admin</Link>
        </div>
      </footer>
    </div>
  );
}
